import { useEffect, useMemo, useRef, useState } from "react";
import type { ComponentProps, CSSProperties, FC } from "react";
import {
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import BaseButton from "../../../components/EcommerceActionButton/Button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../components/Sheet";
import useCompanyStore from "../../../Store/supplierCompany/companyData";
import {
  statusLabels,
  validationLabels,
  validationBadgeClass,
} from "../statusLabels";
import { DISPATCH_METHODS, methodLabels } from "../methods";
import styles from "../DispatchesPage.module.scss";
import { useGetAllInventoryProducts } from "../../../utils/react-query/queryInventory/queries";
import { useGetReservation } from "../../../utils/react-query/queryReservations/queries";
import { useGetDispatchAvailableStock } from "../../../utils/react-query/queryDispatches/queries";
import {
  buildAvailableStockQuery,
  indexAvailableStock,
  validateQuantities,
} from "../../../utils/stockAvailability";
import type { StockErrorMap } from "../../../utils/stockAvailability";
import { ProductAutocomplete } from "../../../components/ProductAutocomplete/ProductAutocomplete";
import type {
  CreateDispatchPayload,
  UpdateDispatchPayload,
  Dispatch,
  DispatchMethod,
  DispatchStatus,
  DispatchValidationStatus,
} from "../types";

// Ecommerce Button forwards extra props to the underlying <button> but its
// ButtonProps type omits `style`; extend it locally for the fallback below.
type BrandedButtonProps = ComponentProps<typeof BaseButton> & {
  style?: CSSProperties;
};
const Button: FC<BrandedButtonProps> = BaseButton;

interface DispatchFormProps {
  open: boolean;
  /** If provided, the form is in edit mode and submits via onUpdate (PUT, never POST) */
  dispatch?: Dispatch | null;
  /** Edit mode only: when false the Estado field is read-only (dispatches:change_status) */
  canChangeStatus?: boolean;
  onClose: () => void;
  onCreate?: (payload: CreateDispatchPayload) => Promise<void>;
  onUpdate?: (payload: UpdateDispatchPayload) => Promise<void>;
}

interface ItemRow {
  product_id: number;
  storage_address_id: number;
  quantity: number;
}

const emptyItem: ItemRow = { product_id: 0, storage_address_id: 0, quantity: 0 };

/** Creation default, matches the backend default */
const DEFAULT_DISPATCH_STATUS: DispatchStatus = "order_preparation";

/** Helper under a field the backend locked for this editor (S4/S5, S7) */
const LOCKED_FIELD_HELPER = "Sólo lectura para tu rol";
/** Items helpers in edit mode (S8): advisors may change them, everyone else reads them */
const ITEMS_ADVISOR_HELPER =
  "Cambiar los items vuelve a enviar la validación al Contador";
const ITEMS_READ_ONLY_HELPER =
  "Solo el comercial/asesor puede modificar los items";
/** Estado helper notes: read-only (no permission) and the approval gate (S1/S8) */
const READ_ONLY_STATUS_NOTE = "Sólo lectura: requiere el permiso cambiar estado";
const APPROVAL_STATUS_NOTE = "Requiere aprobación de Contaduría";

/** Backend locked-field keys (S9) rendered by this form; items are gated by `items_editable` (S7) */
type LockableField =
  | "reservation_id"
  | "quote_number"
  | "recipient_name"
  | "recipient_phone"
  | "delivery_address"
  | "invoice_number"
  | "duration_days"
  | "method"
  | "dispatch_date"
  | "tracking_guide"
  | "remission_code"
  | "notes";

export function DispatchForm({
  open,
  dispatch,
  canChangeStatus = false,
  onClose,
  onCreate,
  onUpdate,
}: DispatchFormProps) {
  const isEditMode = !!dispatch;
  // S7/S8: only an advisor on a header-editable status gets item editing. The
  // flag comes from the loaded detail; list rows never carry it.
  const itemsEditable = isEditMode && dispatch?.items_editable === true;
  // Item controls and add/remove render in create mode and for advisor edits;
  // the same gate decides whether items are validated and submitted.
  const canEditItems = !isEditMode || itemsEditable;
  // Logistic fields (method/status/date/guide/remission/invoice/duration) are
  // hidden on create and filled later in edit mode.
  const showLogisticsFields = isEditMode;
  const { companyPrimaryColor } = useCompanyStore();
  // The complete catalog, not one inventory page: GET /v2/inventory is
  // paginated by id ASC, so newly created products live on the last pages.
  const { data: inventory, isLoading: isLoadingInventory } =
    useGetAllInventoryProducts();

  const [reservationId, setReservationId] = useState(
    dispatch?.reservation_id != null ? String(dispatch.reservation_id) : ""
  );
  const [quotation, setQuotation] = useState(
    dispatch?.quote_number ?? dispatch?.reservation?.quote_number ?? ""
  );
  // Quotation of the typed reservation, used as reference + match validation.
  // Quotation module doesn't exist yet (TPP pending): free text is allowed
  // when there is no reservation.
  const reservationNumericId = /^\d+$/.test(reservationId.trim())
    ? parseInt(reservationId.trim(), 10)
    : 0;
  const { data: reservationDetail } = useGetReservation(reservationNumericId);
  const reservationQuote = reservationDetail?.quote_number ?? null;
  const prevReservationQuote = useRef<string | null>(null);
  useEffect(() => {
    // Auto-fill from the reservation while the field is empty or untouched
    // (still showing the previous reference). Manual edits are preserved.
    setQuotation((prev) =>
      reservationQuote &&
      (prev === "" || prev === prevReservationQuote.current)
        ? reservationQuote
        : prev
    );
    prevReservationQuote.current = reservationQuote;
  }, [reservationQuote]);
  const [method, setMethod] = useState<string>(dispatch?.method ?? "");
  const [status, setStatus] = useState<DispatchStatus>(
    dispatch?.status ?? DEFAULT_DISPATCH_STATUS
  );
  const [recipientName, setRecipientName] = useState(dispatch?.recipient_name ?? "");
  const [recipientPhone, setRecipientPhone] = useState(dispatch?.recipient_phone ?? "");
  const [deliveryAddress, setDeliveryAddress] = useState(dispatch?.delivery_address ?? "");
  const [notes, setNotes] = useState(dispatch?.notes ?? "");
  // Touched tracking so the "required" error shows after interacting, not on
  // first paint (mirrors the DispatchValidationDialog pattern).
  const [notesTouched, setNotesTouched] = useState(false);
  const [methodTouched, setMethodTouched] = useState(false);
  const [dispatchDate, setDispatchDate] = useState(dispatch?.dispatch_date ?? "");
  const [dispatchDateTouched, setDispatchDateTouched] = useState(false);
  const [trackingGuide, setTrackingGuide] = useState(dispatch?.tracking_guide ?? "");
  const [trackingGuideTouched, setTrackingGuideTouched] = useState(false);
  const [remissionCode, setRemissionCode] = useState(dispatch?.remission_code ?? "");
  const [remissionCodeTouched, setRemissionCodeTouched] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(dispatch?.invoice_number ?? "");
  const [durationDays, setDurationDays] = useState(
    dispatch?.duration_days != null ? String(dispatch.duration_days) : ""
  );
  const [items, setItems] = useState<ItemRow[]>(
    dispatch?.items?.map((i) => ({
      product_id: i.product_id,
      storage_address_id: i.storage_address_id,
      quantity: i.quantity,
    })) ?? [{ ...emptyItem }]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Per-row availability errors, keyed by item index. Validated when the form
  // submits items (create or advisor edit); a read-only edit shows the
  // availability but is never blocked by a stock rule it is not sending.
  const [stockErrors, setStockErrors] = useState<StockErrorMap>({});

  // Debounce the available-stock query 300ms after the items change so the
  // advisor can finish selecting product + bodega before we hit the endpoint.
  const [debouncedStockQuery, setDebouncedStockQuery] = useState<string | null>(
    null
  );
  useEffect(() => {
    const query = buildAvailableStockQuery(items);
    const timer = setTimeout(() => setDebouncedStockQuery(query), 300);
    return () => clearTimeout(timer);
  }, [items]);

  // Units available per location for the rows shown (create: the rows being
  // typed; edit: the dispatch's items), from GET /v2/dispatches/available-stock
  // (same rule as reservations: physical stock minus approved reservations).
  // In an edit without `items_editable` it is informative only: never blocking.
  const { data: stockData, isFetching: isFetchingStock } =
    useGetDispatchAvailableStock(debouncedStockQuery);

  // Lookup of "product_id:storage_address_id" -> available_stock.
  const availableMap = useMemo(
    () => indexAvailableStock(stockData?.items ?? []),
    [stockData]
  );

  // L5 (odd/tasks/dispatch-items-revalidation.md): while the approval's stock
  // discount is still in effect, the item PUT releases those units before
  // replacing the rows. The form must validate and display what the pair will
  // offer at replacement time, otherwise the dispatch's own reservation blocks
  // a legitimate increase (stock 10, dispatch 7 → the API reports 3). The
  // server releases the persisted rows, so the sum uses `dispatch.items` (the
  // loaded detail), never the edited state.
  const heldQuantities = useMemo(() => {
    const map = new Map<string, number>();
    if (!itemsEditable || dispatch?.items_stock_held !== true) return map;
    for (const item of dispatch.items ?? []) {
      const key = `${item.product_id}:${item.storage_address_id}`;
      map.set(key, (map.get(key) ?? 0) + item.quantity);
    }
    return map;
  }, [dispatch, itemsEditable]);

  // Availability used by BOTH the «Stock disponible» caption and the stock
  // validation: the base map is kept untouched and a copy is widened with the
  // released units. A pair the API did not price keeps deferring to the backend.
  const effectiveAvailableMap = useMemo(() => {
    if (heldQuantities.size === 0) return availableMap;
    const map = new Map(availableMap);
    heldQuantities.forEach((heldQuantity, key) => {
      const available = map.get(key);
      if (available !== undefined) map.set(key, available + heldQuantity);
    });
    return map;
  }, [availableMap, heldQuantities]);

  // Re-run client-side validation whenever the items or the stock data change.
  // Create and advisor edits validate; a read-only edit only displays.
  useEffect(() => {
    setStockErrors(
      canEditItems ? validateQuantities(items, effectiveAvailableMap) : {}
    );
  }, [items, effectiveAvailableMap, canEditItems]);

  const hasStockErrors = Object.keys(stockErrors).length > 0;

  const isMethodValid =
    method.trim().length > 0 &&
    (DISPATCH_METHODS as string[]).includes(method.trim());
  const recipientTrimmed = recipientName.trim();
  const isRecipientValid =
    recipientTrimmed.length >= 1 && recipientTrimmed.length <= 255;
  const quotationTrimmed = quotation.trim();
  const isQuotationValid =
    quotationTrimmed.length >= 1 && quotationTrimmed.length <= 50;
  const isQuotationMismatch =
    !!reservationQuote &&
    quotationTrimmed !== "" &&
    quotationTrimmed !== reservationQuote;
  const isItemsValid =
    items.length > 0 &&
    items.every(
      (item) =>
        item.product_id > 0 &&
        item.storage_address_id > 0 &&
        Number.isInteger(item.quantity) &&
        item.quantity >= 1
    );
  const durationTrimmed = durationDays.trim();
  const isDurationValid =
    durationTrimmed === "" ||
    (/^\d+$/.test(durationTrimmed) && parseInt(durationTrimmed, 10) >= 0);
  // Observations by role (S1/S3): every stage must carry its observation —
  // the advisor on create, Logística on edit. Enforced here in the form
  // rather than in the Joi schema so the API keeps accepting partial PUTs.
  const notesTrimmed = notes.trim();
  const isNotesValid = notesTrimmed.length >= 1 && notesTrimmed.length <= 500;

  // Read-only Validación: edit mode shows the backend value; create mode is
  // derived from the linked reservation (non-empty numeric id => reserved).
  // It is never part of the submitted payload.
  const validationStatus: DispatchValidationStatus = isEditMode
    ? dispatch?.validation_status ?? "pending"
    : /^\d+$/.test(reservationId.trim())
    ? "reserved"
    : "pending";

  // Locked fields (S4/S5): the backend lists the keys this editor may not change.
  const lockedFields = dispatch?.locked_fields ?? [];
  const isLocked = (field: LockableField) => lockedFields.includes(field);
  // Approval gate (S1/S8): Despachado is only selectable once Contaduría approved.
  const isDispatchApproved = validationStatus === "approved";
  // S2: in edit mode, Estado = Despachado makes these fields mandatory.
  const requiresDispatchFields = isEditMode && status === "dispatched";
  const isDispatchDateValid = dispatchDate.trim().length > 0;
  const isTrackingGuideValid = trackingGuide.trim().length > 0;
  const isRemissionCodeValid = remissionCode.trim().length > 0;
  // S7: a locked method is not required — a dispatch created by an advisor is
  // born without one, so the advisor could never save the edit otherwise.
  const isMethodRequired = !isLocked("method");
  // S7: a locked field can never be mandatory, so the observation is only
  // required while it is editable; the backend still enforces it when the
  // dispatch is marked as Despachado.
  const isNotesEditable = !isLocked("notes");
  const showMethodError = isMethodRequired && methodTouched && !isMethodValid;
  const showDispatchDateError =
    requiresDispatchFields && dispatchDateTouched && !isDispatchDateValid;
  const showTrackingGuideError =
    requiresDispatchFields && trackingGuideTouched && !isTrackingGuideValid;
  const showRemissionCodeError =
    requiresDispatchFields && remissionCodeTouched && !isRemissionCodeValid;

  // Edit mode uses PUT: the payload never carries reservation_id, and carries
  // items only when the advisor may change them (S8). When the items are
  // read-only they are not sent, so they never block Guardar.
  // The list endpoint omits items; the Page loads the detail on edit.
  const canConfirm =
    (!isEditMode || !isMethodRequired || isMethodValid) &&
    isRecipientValid &&
    isQuotationValid &&
    !isQuotationMismatch &&
    (!isNotesEditable || isNotesValid) &&
    (!requiresDispatchFields ||
      (isDispatchDateValid && isTrackingGuideValid && isRemissionCodeValid)) &&
    (!canEditItems || (isItemsValid && !hasStockErrors)) &&
    (!isEditMode || isDurationValid) &&
    !isLoadingInventory &&
    !isSubmitting;

  // Local fallback (F7b): the company store is empty in the Dispatches
  // context, so brand the primary action with MUI default primary blue.
  const primaryFallbackStyle = companyPrimaryColor
    ? undefined
    : { backgroundColor: "#3f51b5" };

  const handleItemChange = (index: number, field: keyof ItemRow, value: number) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        // Edit mode uses PUT: never POST, never send reservation_id. `items`
        // is included only when the advisor may change them (S8).
        // `status` is only sent when the caller holds dispatches:change_status;
        // the backend rejects the change otherwise.
        if (!dispatch || !onUpdate) return;
        // Locked fields resend the loaded value: the PUT still requires
        // quote_number and recipient_name, and rejects any change with 400.
        // The method is omitted when the effective value is empty: the PUT
        // schema rejects ""/null, and a locked method must not block saving a
        // dispatch created without one (S7).
        const effectiveMethod = isLocked("method")
          ? dispatch.method ?? ""
          : method.trim();
        const payload: UpdateDispatchPayload = {
          ...(effectiveMethod
            ? { method: effectiveMethod as DispatchMethod }
            : {}),
          ...(canChangeStatus ? { status } : {}),
          recipient_name: isLocked("recipient_name")
            ? dispatch.recipient_name
            : recipientTrimmed,
          quote_number: isLocked("quote_number")
            ? dispatch.quote_number ?? ""
            : quotationTrimmed,
          recipient_phone: isLocked("recipient_phone")
            ? dispatch.recipient_phone
            : recipientPhone.trim() || null,
          delivery_address: isLocked("delivery_address")
            ? dispatch.delivery_address
            : deliveryAddress.trim() || null,
          notes: isLocked("notes") ? dispatch.notes ?? null : notes.trim() || null,
          dispatch_date: dispatchDate.trim() || null,
          tracking_guide: trackingGuide.trim() || null,
          remission_code: remissionCode.trim() || null,
          invoice_number: isLocked("invoice_number")
            ? dispatch.invoice_number
            : invoiceNumber.trim() || null,
          duration_days: isLocked("duration_days")
            ? dispatch.duration_days
            : durationTrimmed === ""
            ? null
            : parseInt(durationTrimmed, 10),
          ...(itemsEditable
            ? {
                items: items.map((item) => ({
                  product_id: item.product_id,
                  storage_address_id: item.storage_address_id,
                  quantity: item.quantity,
                })),
              }
            : {}),
        };
        await onUpdate(payload);
        return;
      }
      if (!onCreate) return;
      const reservationTrimmed = reservationId.trim();
      // Create mode sends only the base header: method/status/logistics are
      // filled later by Logística/Bodega in edit mode.
      const payload: CreateDispatchPayload = {
        reservation_id:
          reservationTrimmed === "" ? null : parseInt(reservationTrimmed, 10),
        recipient_name: recipientTrimmed,
        quote_number: quotationTrimmed,
        recipient_phone: recipientPhone.trim() || null,
        delivery_address: deliveryAddress.trim() || null,
        notes: notes.trim() || null,
        items: items.map((item) => ({
          product_id: item.product_id,
          storage_address_id: item.storage_address_id,
          quantity: item.quantity,
        })),
      };
      await onCreate(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>
            {isEditMode ? "Editar Despacho" : "Nuevo Despacho"}
          </SheetTitle>
          <p
            style={{ fontSize: 14, color: "#6b7280", margin: "4px 0 0" }}
          >
            {isEditMode
              ? "Modifica los datos del despacho seleccionado."
              : "Completa la información para registrar un nuevo despacho."}
          </p>
        </SheetHeader>

        <div style={{ padding: 24 }}>
        <TextField
          label="Reserva (opcional)"
          variant="outlined"
          fullWidth
          margin="normal"
          value={reservationId}
          onChange={(e) => setReservationId(e.target.value)}
          placeholder="ID de reserva o vacío"
          inputProps={{ "aria-label": "reservation-id", inputMode: "numeric" }}
          disabled={isLocked("reservation_id")}
          helperText={
            isLocked("reservation_id")
              ? LOCKED_FIELD_HELPER
              : "Vacío = despacho manual sin reserva"
          }
        />

        <TextField
          label={isLocked("quote_number") ? "Cotización" : "Cotización *"}
          variant="outlined"
          fullWidth
          margin="normal"
          value={quotation}
          onChange={(e) => setQuotation(e.target.value)}
          placeholder="Número de cotización"
          inputProps={{ maxLength: 50, "aria-label": "quote-number" }}
          disabled={isLocked("quote_number")}
          error={isQuotationMismatch || quotation.length > 50}
          helperText={
            isQuotationMismatch
              ? `Debe coincidir con la reserva (${reservationQuote})`
              : isLocked("quote_number")
              ? LOCKED_FIELD_HELPER
              : reservationQuote
              ? `De la reserva: ${reservationQuote}`
              : "Requerida, máximo 50 caracteres"
          }
        />

        {showLogisticsFields && (
          <>
            <TextField
              select
              SelectProps={{ native: true }}
              label={
                isMethodRequired ? "Método de entrega *" : "Método de entrega"
              }
              variant="outlined"
              fullWidth
              margin="normal"
              value={method}
              onChange={(e) => {
                setMethod(e.target.value);
                setMethodTouched(true);
              }}
              onBlur={() => setMethodTouched(true)}
              disabled={isLocked("method")}
              error={showMethodError}
              helperText={
                showMethodError
                  ? "El método de entrega es obligatorio"
                  : isLocked("method")
                  ? LOCKED_FIELD_HELPER
                  : undefined
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "method" }}
            >
              <option value="">Seleccionar método</option>
              {DISPATCH_METHODS.map((m) => (
                <option key={m} value={m}>
                  {methodLabels[m]}
                </option>
              ))}
            </TextField>

            <TextField
              select
              SelectProps={{ native: true }}
              label="Estado"
              variant="outlined"
              fullWidth
              margin="normal"
              value={status}
              onChange={(e) => setStatus(e.target.value as DispatchStatus)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "status" }}
              disabled={isEditMode && !canChangeStatus}
              helperText={
                !isDispatchApproved ? (
                  <>
                    {isEditMode && !canChangeStatus && (
                      <span style={{ display: "block" }}>
                        {READ_ONLY_STATUS_NOTE}
                      </span>
                    )}
                    <span style={{ display: "block" }}>
                      {APPROVAL_STATUS_NOTE}
                    </span>
                  </>
                ) : isEditMode && !canChangeStatus ? (
                  READ_ONLY_STATUS_NOTE
                ) : undefined
              }
            >
              {(Object.keys(statusLabels) as DispatchStatus[]).map((s) => (
                <option
                  key={s}
                  value={s}
                  disabled={s === "dispatched" && !isDispatchApproved}
                >
                  {statusLabels[s]}
                </option>
              ))}
            </TextField>
          </>
        )}

        <TextField
          label={
            isLocked("recipient_name")
              ? "Nombre del cliente"
              : "Nombre del cliente *"
          }
          variant="outlined"
          fullWidth
          margin="normal"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          disabled={isLocked("recipient_name")}
          placeholder="Nombre de quien recibe"
          inputProps={{ maxLength: 255, "aria-label": "recipient-name" }}
          error={recipientName.length > 255}
          helperText={
            isLocked("recipient_name")
              ? LOCKED_FIELD_HELPER
              : recipientName.length > 255
              ? "Máximo 255 caracteres"
              : "Requerido, máximo 255 caracteres"
          }
        />

        <TextField
          label="Teléfono del cliente"
          variant="outlined"
          fullWidth
          margin="normal"
          value={recipientPhone}
          onChange={(e) => setRecipientPhone(e.target.value)}
          disabled={isLocked("recipient_phone")}
          helperText={
            isLocked("recipient_phone") ? LOCKED_FIELD_HELPER : undefined
          }
          inputProps={{ maxLength: 20, "aria-label": "recipient-phone" }}
        />

        <TextField
          label="Ciudad / Dirección de entrega"
          variant="outlined"
          fullWidth
          margin="normal"
          value={deliveryAddress}
          onChange={(e) => setDeliveryAddress(e.target.value)}
          disabled={isLocked("delivery_address")}
          helperText={
            isLocked("delivery_address") ? LOCKED_FIELD_HELPER : undefined
          }
          placeholder="Ciudad y dirección"
          inputProps={{ "aria-label": "delivery-address" }}
        />

        <div style={{ marginTop: 16 }}>
          <Typography
            variant="body2"
            style={{ fontSize: 12, color: "#6b7280" }}
          >
            Validación
          </Typography>
          <div style={{ marginTop: 4 }}>
            <span
              className={`${styles.badge} ${validationBadgeClass[validationStatus]}`}
            >
              {validationLabels[validationStatus]}
            </span>
          </div>
        </div>

        {showLogisticsFields && (
          <>
            <TextField
              label={
                requiresDispatchFields && !isLocked("dispatch_date")
                  ? "Fecha de despacho *"
                  : "Fecha de despacho"
              }
              type="date"
              variant="outlined"
              fullWidth
              margin="normal"
              value={dispatchDate}
              onChange={(e) => {
                setDispatchDate(e.target.value);
                setDispatchDateTouched(true);
              }}
              onBlur={() => setDispatchDateTouched(true)}
              disabled={isLocked("dispatch_date")}
              error={showDispatchDateError}
              helperText={
                showDispatchDateError
                  ? "La fecha de despacho es obligatoria al despachar"
                  : isLocked("dispatch_date")
                  ? LOCKED_FIELD_HELPER
                  : undefined
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "dispatch-date" }}
            />

            <TextField
              label={
                requiresDispatchFields && !isLocked("tracking_guide")
                  ? "Guía *"
                  : "Guía"
              }
              variant="outlined"
              fullWidth
              margin="normal"
              value={trackingGuide}
              onChange={(e) => {
                setTrackingGuide(e.target.value);
                setTrackingGuideTouched(true);
              }}
              onBlur={() => setTrackingGuideTouched(true)}
              disabled={isLocked("tracking_guide")}
              error={showTrackingGuideError}
              helperText={
                showTrackingGuideError
                  ? "La guía es obligatoria al despachar"
                  : isLocked("tracking_guide")
                  ? LOCKED_FIELD_HELPER
                  : undefined
              }
              inputProps={{ maxLength: 255, "aria-label": "tracking-guide" }}
            />

            <TextField
              label={
                requiresDispatchFields && !isLocked("remission_code")
                  ? "Remisión *"
                  : "Remisión"
              }
              variant="outlined"
              fullWidth
              margin="normal"
              value={remissionCode}
              onChange={(e) => {
                setRemissionCode(e.target.value);
                setRemissionCodeTouched(true);
              }}
              onBlur={() => setRemissionCodeTouched(true)}
              disabled={isLocked("remission_code")}
              error={showRemissionCodeError}
              helperText={
                showRemissionCodeError
                  ? "La remisión es obligatoria al despachar"
                  : isLocked("remission_code")
                  ? LOCKED_FIELD_HELPER
                  : undefined
              }
              inputProps={{ maxLength: 100, "aria-label": "remission-code" }}
            />

            <TextField
              label="Factura"
              variant="outlined"
              fullWidth
              margin="normal"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              disabled={isLocked("invoice_number")}
              helperText={
                isLocked("invoice_number") ? LOCKED_FIELD_HELPER : undefined
              }
              inputProps={{ maxLength: 100, "aria-label": "invoice-number" }}
            />

            <TextField
              label="Duración (días)"
              type="number"
              variant="outlined"
              fullWidth
              margin="normal"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              disabled={isLocked("duration_days")}
              helperText={
                isLocked("duration_days") ? LOCKED_FIELD_HELPER : undefined
              }
              inputProps={{ min: 0, "aria-label": "duration-days" }}
            />
          </>
        )}

        <TextField
          label={
            requiresDispatchFields && isNotesEditable
              ? "Observación *"
              : "Observación"
          }
          variant="outlined"
          fullWidth
          multiline
          minRows={2}
          margin="normal"
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setNotesTouched(true);
          }}
          onBlur={() => setNotesTouched(true)}
          disabled={!isNotesEditable}
          error={isNotesEditable && notesTouched && !isNotesValid}
          helperText={
            !isNotesEditable
              ? LOCKED_FIELD_HELPER
              : notesTouched && !isNotesValid
              ? "La observación es obligatoria"
              : "Observación propia de esta etapa; queda en el historial"
          }
          inputProps={{ "aria-label": "notes" }}
        />

        <Typography variant="subtitle2" style={{ marginTop: 16 }}>
          Items
        </Typography>
        {isEditMode && (
          <Typography variant="caption" color="textSecondary" display="block">
            {itemsEditable ? ITEMS_ADVISOR_HELPER : ITEMS_READ_ONLY_HELPER}
          </Typography>
        )}

        {items.map((item, index) => (
          <div key={index} style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <ProductAutocomplete
              products={inventory?.products ?? []}
              value={item.product_id}
              onChange={(productId) =>
                handleItemChange(index, "product_id", productId)
              }
              inputAriaLabel={`product-id-${index}`}
              disabled={!canEditItems || isLoadingInventory}
              style={{ minWidth: 180, flex: 1 }}
            />
            <TextField
              select
              SelectProps={{ native: true }}
              label="Bodega"
              variant="outlined"
              size="small"
              value={item.storage_address_id || ""}
              onChange={(e) =>
                handleItemChange(index, "storage_address_id", parseInt(e.target.value, 10) || 0)
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": `address-id-${index}` }}
              disabled={!canEditItems || isLoadingInventory}
              style={{ minWidth: 160, flex: 1 }}
            >
              <option value={0}>Seleccionar bodega</option>
              {inventory?.storageAddresses.map((address: any) => (
                <option key={address.id} value={address.id}>
                  {address.name}
                </option>
              ))}
            </TextField>
            <TextField
              label="Cantidad"
              type="number"
              variant="outlined"
              size="small"
              value={item.quantity || ""}
              onChange={(e) =>
                handleItemChange(index, "quantity", parseInt(e.target.value, 10) || 0)
              }
              inputProps={{ min: 1, "aria-label": `quantity-${index}` }}
              disabled={!canEditItems}
              style={{ width: 120 }}
            />
            {canEditItems && (
              <IconButton
                aria-label="Eliminar item"
                onClick={() => removeItem(index)}
                disabled={items.length <= 1}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
            <div className={styles.stockInfo}>
              {stockErrors[index] ? (
                <Typography
                  variant="caption"
                  color="error"
                  role="alert"
                  aria-label={`stock-error-${index}`}
                >
                  {stockErrors[index]}
                </Typography>
              ) : (
                item.product_id > 0 &&
                item.storage_address_id > 0 &&
                effectiveAvailableMap.has(
                  `${item.product_id}:${item.storage_address_id}`
                ) && (
                  <Typography
                    variant="caption"
                    aria-label={`stock-available-${index}`}
                  >
                    Stock disponible:{" "}
                    {
                      effectiveAvailableMap.get(
                        `${item.product_id}:${item.storage_address_id}`
                      )
                    }
                  </Typography>
                )
              )}
              {isFetchingStock && !stockErrors[index] && (
                <Typography variant="caption">
                  Consultando stock…
                </Typography>
              )}
            </div>
          </div>
        ))}

        {canEditItems && (
          <Button
            color="outlined"
            size="small"
            icon={AddIcon}
            onClick={addItem}
            style={{ marginTop: 8 }}
          >
            Agregar item
          </Button>
        )}
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            padding: "16px 24px",
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Button
            size="medium"
            color="outlined"
            shape="little-rounded"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            size="medium"
            color="primary"
            shape="little-rounded"
            onClick={handleConfirm}
            disabled={!canConfirm}
            isLoading={isSubmitting}
            style={primaryFallbackStyle}
          >
            {isEditMode ? "Guardar" : "Crear"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default DispatchForm;
